/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Connect a Gmail or Outlook mailbox from anywhere in the app.
 *
 * Shared by Profile > Email (EmailSettings) and the inbox's empty state, so
 * both offer the same providers and run the same OAuth popup.
 *
 * Flow: open a blank popup synchronously (a popup opened after an await is
 * blocked by Safari and Firefox), ask the backend for the provider's consent
 * URL, point the popup at it. The backend's callback page postMessages
 * `{ type: 'pearl-email-oauth', success, ... }` to this window and closes.
 * If that message never arrives (the opener link can be severed by the
 * provider's pages), closing the popup still triggers `onPopupClosed`, which
 * callers use to re-check the connection.
 *
 * Which providers are offered: the build-time REACT_APP_EMAIL_PROVIDERS list,
 * narrowed by GET /email-settings/providers so a provider whose OAuth app is
 * not configured on the backend is hidden. If that call fails, the env list
 * stands on its own rather than hiding everything.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from 'auth/Auth';
import { getApiBaseUrl } from 'utils/apiBaseUrl';

export const OAUTH_MESSAGE_TYPE = 'pearl-email-oauth';

export const GMAIL_LOGO =
  'https://res.cloudinary.com/dhtfyxzcp/image/upload/t_Thumbnail/v1739719517/Gmail_icon__282020_29_ttb912.svg';
export const OUTLOOK_LOGO =
  'https://res.cloudinary.com/dhtfyxzcp/image/upload/t_Thumbnail/v1739719533/800px-Microsoft_Office_Outlook__282018_E2_80_93present_29.svg_oi52cx.png';

export const EMAIL_PROVIDERS = {
  gmail: {
    key: 'gmail',
    label: 'Gmail',
    logo: GMAIL_LOGO,
    description: 'Google Workspace or a personal Gmail account',
    loginPath: '/auth/google/login',
  },
  outlook: {
    key: 'outlook',
    label: 'Outlook',
    logo: OUTLOOK_LOGO,
    description: 'Microsoft 365, Outlook.com or Hotmail',
    loginPath: '/auth/outlook/login',
  },
};

/** The connected-account record's `email_provider` ('Gmail' | 'Outlook') as a provider entry. */
export const providerForAccount = (emailProvider) =>
  EMAIL_PROVIDERS[String(emailProvider || '').toLowerCase()] || null;

/** Parse REACT_APP_EMAIL_PROVIDERS: "gmail" | "outlook" | "both" | "gmail,outlook" (default: both) */
export function getEnabledEmailProviders(raw = process.env.REACT_APP_EMAIL_PROVIDERS) {
  const value = (raw || 'both').trim().toLowerCase();
  if (value === 'both' || value === 'all') {
    return { gmail: true, outlook: true };
  }
  const parts = value.split(/[,|\s]+/).map((p) => p.trim()).filter(Boolean);
  return {
    gmail: parts.includes('gmail') || parts.includes('google'),
    outlook: parts.includes('outlook') || parts.includes('microsoft'),
  };
}

const originOf = (url) => {
  try {
    return new URL(url).origin;
  } catch {
    return null;
  }
};

/**
 * Is this a result from our own OAuth callback page?
 *
 * The origin must match exactly. The old check, `event.origin.includes(<last
 * hostname label>)`, took "localhost" or "in" from the page's hostname and so
 * accepted messages from nearly any site.
 */
export function isTrustedOAuthMessage(event, allowedOrigins) {
  if (!event || !allowedOrigins || !allowedOrigins.has(event.origin)) return false;
  const data = event.data;
  if (!data || typeof data !== 'object' || typeof data.success !== 'boolean') return false;
  // Older callback pages did not tag their messages; accept those by shape.
  return data.type === undefined || data.type === OAUTH_MESSAGE_TYPE;
}

const POPUP_WIDTH = 600;
const POPUP_HEIGHT = 680;

export default function useEmailProviderConnect({ onConnected, onFailed, onPopupClosed } = {}) {
  const { axiosInstance, isAuthReady, isAuthenticated } = useAuth();
  const [serverProviders, setServerProviders] = useState(null);
  const [providersLoading, setProvidersLoading] = useState(true);
  const [connecting, setConnecting] = useState(null);
  const [error, setError] = useState('');

  const popupRef = useRef(null);
  const pollRef = useRef(null);
  const allowedOriginsRef = useRef(new Set([originOf(getApiBaseUrl())].filter(Boolean)));
  const callbacksRef = useRef({ onConnected, onFailed, onPopupClosed });
  callbacksRef.current = { onConnected, onFailed, onPopupClosed };

  useEffect(() => {
    if (!isAuthReady || !isAuthenticated || !axiosInstance) return undefined;
    let cancelled = false;
    setProvidersLoading(true);
    axiosInstance
      .get('/email-settings/providers')
      .then((response) => {
        if (!cancelled && response.data && typeof response.data === 'object') {
          setServerProviders(response.data);
        }
      })
      .catch(() => {
        // Older backends have no such endpoint; fall back to the env list.
      })
      .finally(() => {
        if (!cancelled) setProvidersLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [axiosInstance, isAuthReady, isAuthenticated]);

  const providers = useMemo(() => {
    const enabled = getEnabledEmailProviders();
    return Object.values(EMAIL_PROVIDERS).filter(
      (provider) => enabled[provider.key] && (!serverProviders || serverProviders[provider.key] !== false)
    );
  }, [serverProviders]);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const finish = useCallback(() => {
    stopPolling();
    popupRef.current = null;
    setConnecting(null);
  }, [stopPolling]);

  useEffect(() => {
    const handleMessage = (event) => {
      if (!popupRef.current || !isTrustedOAuthMessage(event, allowedOriginsRef.current)) return;
      const provider = event.data.provider || null;
      finish();
      if (event.data.success) {
        setError('');
        callbacksRef.current.onConnected?.(provider);
      } else {
        const message = event.data.error || 'The mailbox could not be connected.';
        setError(message);
        callbacksRef.current.onFailed?.(message, provider);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [finish]);

  useEffect(
    () => () => {
      stopPolling();
    },
    [stopPolling]
  );

  const cancel = useCallback(() => {
    const popup = popupRef.current;
    finish();
    if (popup && !popup.closed) popup.close();
  }, [finish]);

  const connect = useCallback(
    async (providerKey) => {
      const provider = EMAIL_PROVIDERS[providerKey];
      if (!provider || !axiosInstance) return;

      setError('');
      const left = window.screenX + (window.outerWidth - POPUP_WIDTH) / 2;
      const top = window.screenY + (window.outerHeight - POPUP_HEIGHT) / 2;
      if (popupRef.current && !popupRef.current.closed) popupRef.current.close();
      stopPolling();

      const popup = window.open(
        '',
        `pearl-connect-${provider.key}`,
        `width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${left},top=${top},popup=1`
      );
      if (!popup) {
        const message = 'Your browser blocked the sign-in window. Allow pop-ups for this site and try again.';
        setError(message);
        callbacksRef.current.onFailed?.(message, provider.key);
        return;
      }
      popupRef.current = popup;
      setConnecting(provider.key);

      try {
        const response = await axiosInstance.get(provider.loginPath);
        const url = response.data?.url;
        if (!url) throw new Error('No sign-in address was returned.');
        const callbackOrigin = originOf(response.data?.callback_origin);
        if (callbackOrigin) allowedOriginsRef.current.add(callbackOrigin);
        if (popup.closed) {
          finish();
          return;
        }
        popup.location.href = url;
      } catch (err) {
        if (!popup.closed) popup.close();
        finish();
        const detail = err?.response?.data?.detail;
        const message =
          (typeof detail === 'string' && detail) ||
          `${provider.label} could not be reached. Please try again.`;
        setError(message);
        callbacksRef.current.onFailed?.(message, provider.key);
        return;
      }

      const timer = setInterval(() => {
        if (popupRef.current !== popup) {
          // A newer attempt (or the result message) took over.
          clearInterval(timer);
          return;
        }
        if (popup.closed) {
          finish();
          callbacksRef.current.onPopupClosed?.(provider.key);
        }
      }, 800);
      pollRef.current = timer;
    },
    [axiosInstance, finish, stopPolling]
  );

  return { providers, providersLoading, connecting, error, connect, cancel, clearError: () => setError('') };
}
